import React, { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Chip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { deleteFilteredArticles } from '../slices/articlesSlice';
import ConfirmDialog from './common/ConfirmDialog';
import { Delete as DeleteIcon, Schedule as ScheduleIcon, Article as ArticleIcon } from '@mui/icons-material';
import { DeleteFilteredArticlesResponse } from '../api';

interface DeleteFilteredArticlesButtonProps {
  disabled?: boolean;
}

const DeleteFilteredArticlesButton: React.FC<DeleteFilteredArticlesButtonProps> = ({ disabled = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { filters, filteredItems, isLoading } = useSelector((state: RootState) => state.articles);
  
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResults, setDeleteResults] = useState<DeleteFilteredArticlesResponse | null>(null);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  
  const handleOpenConfirmDialog = () => {
    setOpenConfirmDialog(true);
  };
  
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };
  
  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      setOpenConfirmDialog(false);
      
      const resultAction = await dispatch(deleteFilteredArticles(filters));
      
      if (deleteFilteredArticles.fulfilled.match(resultAction)) {
        setDeleteResults(resultAction.payload);
        setShowResultsDialog(true);
      }
    } catch (error) {
      console.error('Error deleting filtered articles:', error);
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleCloseResultsDialog = () => {
    setShowResultsDialog(false);
    setDeleteResults(null);
  };
  
  return (
    <>
      <Button
        variant="outlined"
        color="error"
        startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
        onClick={handleOpenConfirmDialog}
        disabled={disabled || isLoading || isDeleting || filteredItems.length === 0}
      >
        {isDeleting ? 'Deleting...' : 'Delete Filtered Articles'}
      </Button>
      
      {/* Confirm Dialog */}
      <ConfirmDialog
        open={openConfirmDialog}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete all ${filteredItems.length} filtered articles? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseConfirmDialog}
        confirmText="Delete All"
        cancelText="Cancel"
      />
      
      {/* Results Dialog */}
      <Dialog
        open={showResultsDialog}
        onClose={handleCloseResultsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Operation Complete</DialogTitle>
        <DialogContent>
          {deleteResults && (
            <Box sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom color="primary">
                {deleteResults.message}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                <Chip
                  icon={<ArticleIcon />}
                  label={`${deleteResults.articlesDeleted} Articles Deleted`}
                  color="error"
                  variant="outlined"
                />
                <Chip
                  icon={<ScheduleIcon />}
                  label={`${deleteResults.durationSeconds.toFixed(2)} seconds`}
                  color="secondary"
                  variant="outlined"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResultsDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteFilteredArticlesButton;